import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Navigate, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import he from 'he';

import { Body } from '@components/templates';
import { Filters, Worklist, Pagination } from '@components/organisms';
import { atom, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { ADMIN_ACCESS_TOKEN_KEY } from '../../../App';
import { DEFAULT_COUNT_PER_PAGE, DEFAULT_CURRENT_PAGE } from '@components/organisms/Table/Pagination';
import { Input } from '@components/atoms';
import { icons } from '@assets/icons';

interface transferLog {
  type: string;
  sendCompanyName: string;
  receiveCompanyName: string;
  worklistId: number;
  updateDate: string;
}

// log>user>index.txt
const TransferLog = () => {
  const [accessToken, setAccessToken] = useState('');
  const [valueState, setValueState] = useState({
    searchKey: 'SEND',
    searchKeyword: '',
    type: '',
  });
  const [pageConditionsValue, setPageConditionsValue] = useState({
    countPerPage: DEFAULT_COUNT_PER_PAGE,
    currentPage: DEFAULT_CURRENT_PAGE,
    totalCount: 1,
    'order.name': 'id',
    'order.sort': 'DESC',
  });
  const { countPerPage, currentPage } = pageConditionsValue;
  const { searchKey, searchKeyword, type } = valueState;
  const [radioValue, setRadioValue] = useState('');

  useEffect(() => {
    const newToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
    if (newToken) {
      setAccessToken(newToken);
    }
  }, []);

  useEffect(() => {
    if (valueState)
      setPageConditionsValue((prev) => ({
        ...prev,
        currentPage: DEFAULT_CURRENT_PAGE,
      }));
  }, [valueState]);

  const url = `api/transfer/logs/worklist?countPerPage=${countPerPage}&currentPage=${currentPage}&searchType=${
    searchKey || ''
  }&searchValue=${searchKeyword || ''}&type=${type || ''}`;

  const { data } = useSWR(!!accessToken.length ? [{ url, accessToken }] : null);

  let transferLogs: any[] = [];
  if (data) {
    if (data.code === 'UNAUTHORIZED') return <Navigate to={'/'} replace={true} />;

    if (data.code === '200') {
      const { countPerPage, currentPage, totalCount } = data.result.condition;
      const startNo = totalCount - (currentPage - 1) * countPerPage;
      data.result.results.forEach((item: transferLog, index: number) =>
        transferLogs.push({
          no: startNo - index,
          type: item.type,
          send: he.decode(item.sendCompanyName),
          receive: he.decode(item.receiveCompanyName),
          worklist_id: item.worklistId,
          update_date: item.updateDate,
        }),
      );
    }
  }

  const SortTypeList = [
    { label: 'All', type: '' },
    { label: 'Send', type: 'SEND' },
    { label: 'Cancel', type: 'CANCEL' },
  ];

  // Type 체크(Radio)
  const onCheckHandler = (e: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setRadioValue(value);
    setValueState({
      searchKey,
      searchKeyword,
      type: value,
    });
  };

  //검색 새로고침
  const onRefresh = () => {
    setRadioValue('');

    setValueState({
      searchKey: '',
      searchKeyword: '',
      type: '',
    });
  };

  return (
    <>
      <label>Transfer Log</label>
      <Body data-page-type={'table-only'}>
        <StyledFlexColumnsWrapper>
          <StyledSearchBarWrapper>
            <Filters
              searchConditions={valueState}
              setSearchConditions={setValueState}
              conditionType={{
                searchType: [
                  {
                    label: 'Send',
                    value: 'SEND',
                  },
                  {
                    label: 'Receive',
                    value: 'RECEIVE',
                  },
                ],
              }}
              hasRangeType={false}
            />
            <StyledSortTypeBox>
              <strong className={`txt`}>Type</strong>
              <ul className={`type_list`}>
                {SortTypeList.map((list) => {
                  return (
                    <li key={list.label}>
                      <Input
                        type={`radio`}
                        name={`sortType`}
                        value={list.label}
                        onChange={(e) => {
                          onCheckHandler(e, list.type);
                        }}
                        checked={list.type == radioValue}
                      >
                        {list.label}
                      </Input>
                    </li>
                  );
                })}
              </ul>
              <StyledRefreshBtn onClick={onRefresh} />
            </StyledSortTypeBox>
          </StyledSearchBarWrapper>
          <Worklist
            headers={[
              { label: 'No', data_label: 'no' },
              { label: 'Type', data_label: 'type' },
              { label: 'Send', data_label: 'send' },
              { label: 'Receive', data_label: 'receive' },
              { label: 'Worklist ID', data_label: 'worklist_id' },
              { label: 'Update Date', data_label: 'update_date' },
            ]}
            data={{ dataArray: transferLogs }}
          />
          {data && data.result && data.result?.condition.totalCount > 0 && (
            <Pagination conditions={data.result.condition} setConditions={setPageConditionsValue} />
          )}
        </StyledFlexColumnsWrapper>
      </Body>
    </>
  );
};

const StyledFlexColumnsWrapper = styled.main`
  width: 100%;
  table {
    tr[data-highlight='deactivated' i] {
      td {
        background-color: #fafafa;
        color: #999999;
      }
    }

    td > u {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      :hover {
        color: #7fe200;
      }
      b {
        color: #7fe200;
      }
      & > div {
        margin-left: 6px;
        background-color: #ffffff;
      }
      &:hover > div {
        border-color: #7fe200;
        background-color: #effdda;
        :before,
        :after {
          border-color: #7fe200;
        }
      }
      &:active > div {
        border-color: #7fe200;
        background-color: #7fe200;
        :before,
        :after {
          border-color: #ffffff;
        }
      }
    }
  }
`;

const StyledSearchBarWrapper = styled.section`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  & > div:nth-of-type(1) {
    margin-left: 0;
  }
`;
const StyledSortTypeBox = styled.div`
  position: absolute;
  top: 50%;
  left: 600px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  &:before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 1px;
    height: 100%;
    background-color: #e0e0e0;
  }
  & .txt {
    margin-left: 20px;
    font-size: 16px;
  }
  & .type_list {
    position: relative;
    display: flex;
    margin-right: 20px;
    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      right: 0;
      width: 1px;
      height: 100%;
      background-color: #e0e0e0;
    }
    & li {
      margin-left: 20px;
      &:last-child {
        margin-right: 20px;
      }
    }
  }
`;

const StyledRefreshBtn = styled.button`
  width: 16px;
  height: 16px;
  margin: 0;
  padding: 0;
  background: url(${icons.refresh}) no-repeat 50% 50%;
`;

export default TransferLog;
